import { useTranslation } from 'react-i18next'

import { Checkbox } from '@/components/ui/checkbox'
import { Field, FieldDescription, FieldError, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'

import {
  RELATION_OTHER_VALUE,
  RelationSelect,
  type Relation,
  type RelationPerspective,
} from './relation-select'

type Props = {
  idPrefix: string
  // Opts into the "Annan" option, paired with its free-text field below.
  includeOther?: boolean
  isGuardian: boolean
  isLegalRepresentative: boolean
  onIsGuardianChange(next: boolean): void
  onIsLegalRepresentativeChange(next: boolean): void
  onRelationChange(relation: Relation): void
  onRelationOtherChange?(value: string): void
  perspective: RelationPerspective
  relation?: Relation
  relationDescription?: string
  relationError?: string
  relationLabel: string
  relationOther?: string
  relationOtherError?: string
}

export const RelationFieldGroup = ({
  idPrefix,
  includeOther,
  isGuardian,
  isLegalRepresentative,
  onIsGuardianChange,
  onIsLegalRepresentativeChange,
  onRelationChange,
  onRelationOtherChange,
  perspective,
  relation,
  relationDescription,
  relationError,
  relationLabel,
  relationOther,
  relationOtherError,
}: Props) => {
  const { t } = useTranslation()

  // "Jag själv" means the registrant *is* the person — guardian / legal
  // representative don't apply. Hide those fields and clear any stale values
  // so a previously-ticked box isn't silently submitted.
  const handleRelationChange = (next: Relation) => {
    onRelationChange(next)

    if (next === 'self') {
      if (isGuardian) onIsGuardianChange(false)
      if (isLegalRepresentative) onIsLegalRepresentativeChange(false)
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <Field>
        <FieldLabel htmlFor={`${idPrefix}-relation`}>{relationLabel}</FieldLabel>
        <RelationSelect
          id={`${idPrefix}-relation`}
          includeOther={includeOther}
          onValueChange={handleRelationChange}
          perspective={perspective}
          value={relation}
        />
        {relationDescription && <FieldDescription>{relationDescription}</FieldDescription>}
        <FieldError>{relationError}</FieldError>
      </Field>

      {relation === RELATION_OTHER_VALUE && onRelationOtherChange && (
        <Field>
          <FieldLabel htmlFor={`${idPrefix}-relationOther`}>{t('relation.otherInputLabel')}</FieldLabel>
          <Input
            id={`${idPrefix}-relationOther`}
            onChange={event => onRelationOtherChange(event.target.value)}
            placeholder={t('relation.otherInputPlaceholder')}
            value={relationOther ?? ''}
          />
          <FieldError>{relationOtherError}</FieldError>
        </Field>
      )}

      {relation !== 'self' && (
      <div className="flex flex-col gap-4">
        <label
          className="flex items-start gap-3 cursor-pointer"
          htmlFor={`${idPrefix}-isGuardian`}
        >
          <Checkbox
            checked={isGuardian}
            className="mt-0.5"
            id={`${idPrefix}-isGuardian`}
            onCheckedChange={checked => onIsGuardianChange(checked === true)}
          />
          <div className="flex flex-col gap-1">
            <span className="text-sm font-medium leading-none">
              {t('relation.isGuardian.label')}
            </span>
            <span className="text-xs text-muted-foreground">
              {t('relation.isGuardian.description')}
            </span>
          </div>
        </label>

        <label
          className="flex items-start gap-3 cursor-pointer"
          htmlFor={`${idPrefix}-isLegalRepresentative`}
        >
          <Checkbox
            checked={isLegalRepresentative}
            className="mt-0.5"
            id={`${idPrefix}-isLegalRepresentative`}
            onCheckedChange={checked => onIsLegalRepresentativeChange(checked === true)}
          />
          <div className="flex flex-col gap-1">
            <span className="text-sm font-medium leading-none">
              {t('relation.isLegalRepresentative.label')}
            </span>
            <span className="text-xs text-muted-foreground">
              {t('relation.isLegalRepresentative.description')}
            </span>
          </div>
        </label>
      </div>
      )}
    </div>
  )
}
